import {DatePipe} from '@angular/common';
import { AfterViewInit, Component, ElementRef, Input, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { BarController, BarElement, CategoryScale, Decimation, Filler, Legend, Title, Tooltip } from 'chart.js';
import Chart from 'chart.js/auto';
import ChartDataLabels from 'chartjs-plugin-datalabels';

@Component({
    selector: 'app-bar-chart',
    templateUrl: './bar-chart.component.html',
    styleUrl: './bar-chart.component.scss',
    standalone: false
})
export class BarChartComponent implements OnChanges, AfterViewInit {
  @Input() data: any;
  @Input() isBarChartLoder: any;
  @Input() isStacked: boolean = false;
  @Input() customColors: string[] = ['#4CAF50', '#F44336'];
  @Input() chartTitle: string = 'Storage Consumption';
  @Input() isMaterialChart: boolean = false;
  @Input() legendPosition: any = 'bottom';
  @Input() legendAlign: any = 'center';
  @Input() usePointStyle: boolean = false;
  @Input() legendBoxWidth: number = 12;
  @Input() legendFontSize: number = 14;
  @Input() xAxisFontSize: number = 12;
  @Input() xAxisTickPadding: number = 3;
  @Input() xAxisHeight: number = 0;
  @Input() yAxisMaxTicks: number = 8;
  @Input() chartHeight: string = '300px';
  @Input() maintainAspectRatio: boolean = true;
  @Input() showNoDataFound: boolean = false;
  @Input() showTotalLine: boolean = false;
  @Input() totalLineColor: string = '#3580d8';
  @ViewChild('canvas') canvasRef: ElementRef<HTMLCanvasElement>;

  constructor(private readonly datePipe: DatePipe) {
    Chart.register(BarElement, BarController, CategoryScale, Decimation, Filler, Legend, Title, Tooltip, ChartDataLabels);
  }
  public chart: Chart;

  ngAfterViewInit(): void {
    this.renderChart();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.renderChart();
  }

  renderChart(): void {
    if (this.data) {
      const hasData = this.isStacked ? (this.data.length > 0) : this.data.some((x: any) => x.FileSize > 0);
      if (hasData) {
        this.createBarChart(this.data);
      } else if (this.canvasRef?.nativeElement) {
        let chartExists = Chart.getChart(this.canvasRef.nativeElement);
        if (chartExists) {
          chartExists.destroy();
        }
        this.data = [];
      }
    }
  }

  getDataSet(data: any): any {
    let labels: any = [];
    let datasets: any = [];
    if (this.isStacked && data.length > 0 && data[0].datasets) {
      labels = data[0].labels;
      data[0].datasets.forEach((ds: any, index: number) => {
        datasets.push({
          type: 'bar',
          label: ds.label,
          data: ds.data,
          backgroundColor: this.customColors[index] || this.getRandomColor(),
          stack: 'stack1'
        });
      });
      const totals = labels.map((_: any, i: number) => {
        return data[0].datasets.reduce((sum: number, ds: any) => sum + (ds.data[i] || 0), 0);
      });
      datasets.push({
        type: 'bar',
        label: 'Total',
        data: totals.map(() => 0),
        backgroundColor: 'transparent',
        borderColor: 'transparent',
        stack: 'stack1',
        datalabels: {
          display: true,
          align: 'top',
          anchor: 'end',
          color: '#000',
          font: {
            weight: 'bold',
            size: 8
          },
          formatter: (value: any, context: any) => {
            const total = totals[context.dataIndex];
            return total > 0 ? total : '';
          }
        }
      });
      if (this.showTotalLine) {
        datasets.push({
          type: 'line',
          label: 'Total Invoices',
          data: totals,
          borderColor: this.totalLineColor,
          backgroundColor: this.totalLineColor,
          pointRadius: 4,
          tension: 0,
          datalabels: {display: false}
        });
      }
    } else {
      let storageValues: any = [];
      data.forEach((x: any) => {
        labels.push(x.label)
        storageValues.push(x.FileSize);
      });
      datasets.push({
        type: 'bar',
        label: 'Storage',
        data: storageValues,
        fill: false,
        borderColor: this.getRandomColor(),
        backgroundColor: this.getRandomColor()
      });
    }

    if (this.canvasRef?.nativeElement) {
      let chartExists = Chart.getChart(this.canvasRef.nativeElement);
      if (chartExists) {
        chartExists.destroy();
      }
    }
    return {
      labels: labels,
      datasets: datasets
    };
  }

  createBarChart(data: any) {
    let dataSet = this.getDataSet(data);
    let totalStorageFormatted = '';
    if (!this.isStacked) {
      let totalStorage = data.reduce((sum: number, item: any) => sum + item.FileSize, 0);
      totalStorageFormatted = this.converStorage(totalStorage).value;
    }

    if (!this.canvasRef?.nativeElement) return;
    this.chart = new Chart(this.canvasRef.nativeElement, {
      type: "bar",
      data: dataSet,
      options: {
        responsive: true,
        maintainAspectRatio: this.maintainAspectRatio,
        plugins: {
          datalabels: {
            display: (context: any) => {
              if (!this.isStacked) return false;
              if (context.dataset.label === 'Total') return true;
              return context.dataset.data[context.dataIndex] > 0;
            },
            color: '#000',
            anchor: (context: any) => {
              return context.dataset.label === 'Total' ? 'end' : 'center';
            },
            align: (context: any) => {
              return context.dataset.label === 'Total' ? 'top' : 'center';
            },
            offset: 0,
            clip: false,
            clamp: false,
            font: {
              weight: 'bold',
              size: 8
            },
            formatter: (value: number, context: any) => {
              if (context.dataset.label === 'Total') return value;
              if (this.isStacked && value > 0) {
                return value;
              }
              return '';
            }
          },
          legend: {
            position: this.legendPosition,
            align: this.legendAlign,
            labels: {
              usePointStyle: this.usePointStyle,
              boxWidth: this.legendBoxWidth,
              font: {
                size: this.legendFontSize,
              },
              filter: (legendItem: any) => {
                return legendItem.text !== 'Total';
              },
              generateLabels: (chart) => {
                let labels: any = Chart.defaults.plugins.legend.labels.generateLabels(chart);
                if (labels.length > 0 && !this.isStacked) {
                  labels[0].text += ` - Total: ${totalStorageFormatted}`;
                }
                return labels;
              }
            }
          },
          tooltip: {
            callbacks: {
              label: (context) => {
                if (this.isStacked) return context.dataset.label + ': ' + context.parsed.y;
                return this.labelValues(context.parsed.y, context.dataset.label);
              }
            }
          }
        },
        scales: {
          x: {
            stacked: this.isStacked,
            grid: {
              display: false
            },
            afterFit: (scale: any) => {
              if (this.xAxisHeight && this.xAxisHeight > 0) {
                scale.height = this.xAxisHeight;
              }
            },
            ticks: {
              padding: this.xAxisTickPadding,
              font: {
                size: this.xAxisFontSize
              },
              callback: function (value: any, index: number) {
                const label = this.getLabelForValue(value);
                const maxLength = 10;
                if (!label || typeof label !== 'string' || label.length <= maxLength) {
                  return label;
                }
                const result: string[] = [];
                let temp = label;
                while (temp.length > maxLength) {
                  let breakIndex = -1;
                  for (let i = maxLength; i >= 0; i--) {
                    if (temp[i] === ' ' || temp[i] === '_') {
                      breakIndex = i;
                      break;
                    }
                  }
                  if (breakIndex === -1) {
                    result.push(temp.substring(0, maxLength));
                    temp = temp.substring(maxLength);
                  } else if (temp[breakIndex] === '_') {
                      result.push(temp.substring(0, breakIndex + 1));
                      temp = temp.substring(breakIndex + 1);
                    } else {
                      result.push(temp.substring(0, breakIndex));
                      temp = temp.substring(breakIndex + 1);
                  }
                  temp = temp.trimStart();
                }
                if (temp) {
                  result.push(temp);
                }
                return result;
              }
            }
          },
          y: {
            stacked: this.isStacked,
            beginAtZero: true,
            grace: '10%',
            grid: {
              display: false
            },
            ticks: {
              maxTicksLimit: this.yAxisMaxTicks,
              autoSkip: false,
              callback: (value: any, index, ticks) => {
                if (this.isStacked) {
                  if (Number.isInteger(value)) {
                  if (value >= 1000) {
                    return (value / 1000) + 'K';
                  }
                  return value;
                  }
                  return '';
                }
                let modifiedSize: any = this.converStorage(value);
                let filteredArray = ticks.filter((tick: any) => { return tick.label === modifiedSize.value });
                if (filteredArray.length && modifiedSize !== '') {
                  modifiedSize.value = modifiedSize.orgValue.toFixed(1) + modifiedSize.type;
                }
                return modifiedSize.value;
              }
            }
          },
        }
      }
    });
  }

  converStorage(size: any) {
    if (size) {
      if (size < 1024) {
        return {
          value: '0.00 B',
          orgValue: 0,
          type: 'B'
        };
      } else if (size < 1024 * 1024) {
        return {
          value: (size / 1024).toFixed(2) + ' KB',
          orgValue: size / 1024,
          type: 'KB'
        };
      } else if (size < 1024 * 1024 * 1024) {
        return {
          value: (size / (1024 * 1024)).toFixed(2) + ' MB',
          orgValue: size / (1024 * 1024),
          type: 'MB'
        };
      } else {
        return {
          value: (size / (1024 * 1024 * 1024)).toFixed(2) + ' GB',
          orgValue: size / (1024 * 1024 * 1024),
          type: 'GB'
        };
      }
    }
    return {
      value: '0.00 B',
      orgValue: 0,
      type: 'B'
    };
  }

  labelValues(size: any, label: any){
    if (size) {
      if (size < 1024) {
        return label + ': ' + 0 + ' B';
      } else if (size < 1024 * 1024) {
        return label + ': ' + +(size / 1024).toFixed(2) + ' KB';
      } else if (size < 1024 * 1024 * 1024) {
        return label + ': ' + +(size / (1024 * 1024)).toFixed(2) + ' MB';
      } else {
        return label + ': ' + +(size / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
      }
    } else {
      return label + ': ' + '0 MB';
    }
  }

  getRandomColor() {
    const array = new Uint32Array(1);
    crypto.getRandomValues(array);
    const random = array[0] / 2 ** 32;
    return '#' + Math.floor(random * 16777215).toString(16);
  }
}
